"use client";

import { useCallback, useSyncExternalStore } from "react";

// 折りたたみサイドバーの開閉状態を localStorage に覚えるフック。
// 会員向けのサイドメニュー（AppSidebar）と大学管理画面のサイドナビ（UniversitySidebar）が共有する。
//
// localStorage という外部ストアから読むため useSyncExternalStore を使う
// （sessionStorage を読む useCareerStage と同じ流儀）。
// サーバ側の描画では保存値を読めないので展開状態を返し、ハイドレーション後に実際の値へ切り替える。
//
// 読んだ値はキーごとにモジュールへ残す。画面ごとにレイアウトが分かれておりシェルは画面遷移で
// 作り直されるため、都度読み直すと遷移のたびに展開状態から始まって一瞬広がって見える。
// キーで分けるのは、会員向けと大学管理が別々の状態を持つため。
const caches = new Map<string, boolean>();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(): void {
  for (const listener of listeners) listener();
}

// 同じ状態なら同じ値を返す必要があるため、読んだ結果はキャッシュへ確定させる。
function getCollapsed(storageKey: string): boolean {
  const cached = caches.get(storageKey);
  if (cached !== undefined) return cached;

  let value = false;
  try {
    value = localStorage.getItem(storageKey) === "true";
  } catch {
    // プライベートブラウジング等で SecurityError が発生する場合は展開状態のまま扱う
  }
  caches.set(storageKey, value);
  return value;
}

function setCollapsed(storageKey: string, collapsed: boolean): void {
  caches.set(storageKey, collapsed);
  try {
    localStorage.setItem(storageKey, String(collapsed));
  } catch {
    // 保存できなくても、そのセッションの表示は切り替わる
  }
  emit();
}

// サーバ側では保存値を読めない。展開状態から始めて、ハイドレーション後に実際の値へ寄せる。
const getServerCollapsed = () => false;

// ハイドレーションが済んだかどうか。済むまで幅の遷移を止め、縮む様子を見せない。
const getRestored = () => true;
const getServerRestored = () => false;

export interface UseCollapsibleSidebarResult {
  collapsed: boolean;
  /** 保存値の反映が済んだか。済むまで幅の遷移を無効にする。 */
  restored: boolean;
  toggle: () => void;
}

export function useCollapsibleSidebar(
  storageKey: string,
): UseCollapsibleSidebarResult {
  const collapsed = useSyncExternalStore(
    subscribe,
    useCallback(() => getCollapsed(storageKey), [storageKey]),
    getServerCollapsed,
  );
  const restored = useSyncExternalStore(
    subscribe,
    getRestored,
    getServerRestored,
  );

  const toggle = useCallback(() => {
    setCollapsed(storageKey, !getCollapsed(storageKey));
  }, [storageKey]);

  return { collapsed, restored, toggle };
}

// テストが描画をまたいで値を持ち越さないようにするための後始末。
export function resetCollapsedCache(): void {
  caches.clear();
}
